import styled from 'styled-components';
import { motion } from 'framer-motion';
import Container from '../layout/Container';

const About = () => {
  const skills = [
    { category: 'Product Management', items: ['Product Vision & Ideation', 'Full Product Lifecycle Ownership', 'User Research & Feedback Analysis', 'Product Strategy & Positioning', 'Metrics-Driven Decision Making', 'Cost Optimization', 'Performance Optimization'] },
    { category: 'Design & Branding', items: ['Logo Design', 'Website Design & Development', 'UI/UX Design', 'Brand Identity', 'Canva', 'Video Editing'] },
    { category: 'Mobile Development', items: ['React Native', 'Flutter', 'iOS Development', 'Android Development', 'Expo SDK', 'Quality Assurance & Testing', 'Debugging & Issue Resolution'] },
    { category: 'Technical', items: ['TypeScript & JavaScript', 'React', 'FastAPI (Python)', 'PostgreSQL', 'Full-Stack Development'] },
    { category: 'AI & Data', items: ['Machine Learning', 'NLP', 'TensorFlow', 'Financial Modeling', 'AI Stock Analysis', 'Predictive Analytics', 'Data Analysis'] },
    { category: 'DevOps & Infrastructure', items: ['AWS (EC2, RDS, S3, CloudFront, SNS, ElastiCache)', 'Docker', 'CI/CD', 'Redis', 'Build Systems (Xcode, CocoaPods, Gradle, Jenkins)', 'System Architecture'] },
    { category: 'Business & Strategy', items: ['Entrepreneurship', 'Investment Decision-Making', 'ROI Validation', 'Resource Allocation & Budget Management', 'Market Research', 'Digital Marketing'] },
    { category: 'Industry Knowledge', items: ['Sports Tech', 'Hyperlocal', 'Healthtech & Wellness', 'Mobile/Consumer Apps', 'Product Development', 'Market Analysis'] },
  ];

  return (
    <AboutSection id="about">
      <Container>
        <SectionTitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            About Me
          </motion.div>
        </SectionTitle>

        <AboutGrid>
          <AboutContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <AboutText>
                I am a senior product manager and full-stack builder working across sports tech, hyperlocal 
                commerce, and healthtech. As founder at Intergrity-IT Technologies, I lead product strategy, 
                engineering, and growth for GameUp and GoLocal—two live apps that together reached 10,000+ organic 
                users with no marketing spend. I also work with Nidra N3 Limited in the UK as product manager and 
                engineer, delivering their sleep wellness platform from first build through pilot testing.
              </AboutText>
              <AboutText>
                I ship complete products—mobile apps, backends, and the infrastructure around them—often within 
                two to three months. I use AI across a structured workflow from development through testing and 
                deployment, moving quickly without cutting corners on production quality or store readiness.
              </AboutText>
              <AboutText>
                I hold an MBA from Loughborough University and an M.Tech in Computer Science from VIT—combining 
                strategic business leadership with a technical foundation in machine learning and data-driven 
                product decisions. Whether leading a team or working solo on a client engagement, I bring an 
                owner&apos;s mindset, strong user research habits, and a track record of measurable outcomes—from 
                organic growth and infrastructure savings to products now in live pilot with real users.
              </AboutText>
            </motion.div>
          </AboutContent>

          <ProfileContainer>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ProfileImage src="/images/profile-new.png" alt="Christopher Anto" />
            </motion.div>
          </ProfileContainer>
        </AboutGrid>

        <SectionSubtitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            My Skills
          </motion.div>
        </SectionSubtitle>

        <SkillsContainer>
          {skills.map((skillGroup, index) => (
            <SkillGroup
              key={skillGroup.category}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
            >
              <SkillCategory>{skillGroup.category}</SkillCategory>
              <SkillList>
                {skillGroup.items.map((skill) => (
                  <SkillItem key={skill}>{skill}</SkillItem>
                ))}
              </SkillList>
            </SkillGroup>
          ))}
        </SkillsContainer>

        <SectionSubtitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Experience &amp; Education
          </motion.div>
        </SectionSubtitle>

        <ExperienceTimeline>
          <TimelineItem
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
          >
            <TimelineDot />
            <TimelineContent>
              <TimelineDate>May 2024 - Present</TimelineDate>
              <TimelineTitle>Founder &amp; Senior Product Manager</TimelineTitle>
              <TimelineCompany>Intergrity-IT Technologies Pvt Ltd · Remote, India</TimelineCompany>
              <TimelineDescription>
                Lead product strategy, engineering, and growth for two production apps serving 10,000+ users across India. 
                On GameUp, set vision and roadmap for a multi-sport talent platform, ran 50+ user interviews and a VIT Chennai 
                pilot, and grew to 10,000+ organic downloads in eight months with no marketing budget—managing a cross-functional 
                team across engineering, design, and QA. On GoLocal, built the hyperlocal grocery platform solo end to end—full-stack 
                architecture, elderly-first UX, zero-commission model, App Store approval, and major infrastructure savings on AWS, 
                SMS, and data transfer.
              </TimelineDescription>
            </TimelineContent>
          </TimelineItem>

          <TimelineItem
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <TimelineDot />
            <TimelineContent>
              <TimelineDate>Jan 2024 - Present</TimelineDate>
              <TimelineTitle>Product Manager &amp; Full-Stack Engineer</TimelineTitle>
              <TimelineCompany>Nidra N3 Limited · Freelance, United Kingdom</TimelineCompany>
              <TimelineDescription>
                Sole product and engineering lead on a UK sleep wellness platform, taking it from concept to live pilot in under 
                three months. Built the mobile app, FastAPI and PostgreSQL backend, AWS infrastructure, and nidra.io—integrating 
                client-provided EEG headband hardware and a patented sleep algorithm into one seamless experience. Delivered Stripe 
                payments, device order tracking, push notifications, admin dashboard, and onboarding flow. Led roadmap, sprint 
                planning with the founding team, and 15+ user research sessions. App is in pilot testing with early users in the UK.
              </TimelineDescription>
            </TimelineContent>
          </TimelineItem>

          <TimelineItem
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <TimelineDot />
            <TimelineContent>
              <TimelineDate>Sep 2022 - Aug 2023</TimelineDate>
              <TimelineTitle>Market Expansion &amp; Growth Strategy Analyst</TimelineTitle>
              <TimelineCompany>Valueleaf · Greater London, UK (Hybrid)</TimelineCompany>
              <TimelineDescription>
                Full-time role alongside MBA studies at Loughborough. Designed go-to-market approaches for five international 
                markets—covering regulation, competition, and cash flow modelling. Supported a capital raise with financial 
                analysis and investor materials. Researched APAC, MENA, and European opportunities for senior leadership and 
                shaped localization strategies that reduced estimated market-entry risk.
              </TimelineDescription>
            </TimelineContent>
          </TimelineItem>

          <TimelineItem
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <TimelineDot />
            <TimelineContent>
              <TimelineDate>Oct 2021 - Sep 2023</TimelineDate>
              <TimelineTitle>Master of Business Administration (MBA)</TimelineTitle>
              <TimelineCompany>Loughborough University School of Business and Economics</TimelineCompany>
              <TimelineDescription>
                Built core business skills in decision-making, leadership, finance, accounting, strategic change, 
                and people management. Worked with international cohorts on cross-border projects. Dissertation 
                analysed how major tech companies—Amazon, Apple, Microsoft, and Alphabet—perform under economic 
                risk, combining data analysis with strategic planning and cash flow forecasting.
              </TimelineDescription>
            </TimelineContent>
          </TimelineItem>

          <TimelineItem
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <TimelineDot />
            <TimelineContent>
              <TimelineDate>Jul 2016 - Apr 2018</TimelineDate>
              <TimelineTitle>Master of Technology (M.Tech) — Computer Science</TimelineTitle>
              <TimelineCompany>Vellore Institute of Technology</TimelineCompany>
              <TimelineDescription>
                Specialised in machine learning and big data analytics. Used Python and R to turn data into 
                actionable insight—building the technical base I still rely on for product decisions, from 
                algorithm design through metrics and experimentation on live apps.
              </TimelineDescription>
            </TimelineContent>
          </TimelineItem>
        </ExperienceTimeline>
      </Container>
    </AboutSection>
  );
};

const AboutSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  padding-top: ${({ theme }) => theme.spacing.lg}; /* Significantly reduced padding at top to bring section closer to navbar */
  background-color: ${({ theme }) => theme.colors.background};
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(to right, #FFFFFF, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 2rem;
  margin-top: ${({ theme }) => theme.spacing.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AboutText = styled.p`
  font-size: 1.125rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 350px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.boxShadow.elevated};
`;

const SkillsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const SkillGroup = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.boxShadow.normal};
  transition: transform ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const SkillCategory = styled.h4`
  font-size: 1.25rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
`;

const SkillList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SkillItem = styled.li`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  
  &:before {
    content: '•';
    color: ${({ theme }) => theme.colors.secondary};
    font-weight: bold;
    margin-right: ${({ theme }) => theme.spacing.sm};
  }
`;

const ExperienceTimeline = styled.div`
  position: relative;
  margin-left: 20px;
  padding-left: 30px;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: ${({ theme }) => theme.colors.borderColor};
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -36px;
  top: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.backgroundAlt};
`;

const TimelineContent = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.boxShadow.normal};
`;

const TimelineDate = styled.span`
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 0.875rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TimelineTitle = styled.h4`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: 1.25rem;
`;

const TimelineCompany = styled.h5`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TimelineDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.6;
`;

export default About;
